import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { apiErrorMessage } from "../../lib/errors";

interface SourceDraft {
  source_title: string;
  content: string;
}

const EMPTY: SourceDraft = { source_title: "", content: "" };

export default function NewVoicePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [sources, setSources] = useState<SourceDraft[]>([
    { ...EMPTY },
    { ...EMPTY },
    { ...EMPTY },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImportUrl, setShowImportUrl] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importMaxItems, setImportMaxItems] = useState(5);
  const [importing, setImporting] = useState(false);

  function update(i: number, patch: Partial<SourceDraft>) {
    setSources((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }

  function add() {
    setSources((prev) => [...prev, { ...EMPTY }]);
  }

  function remove(i: number) {
    setSources((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function importFromUrl() {
    const u = importUrl.trim();
    if (!u) return;
    setImporting(true);
    try {
      const res = await api.post<{
        samples: { source_title: string | null; content: string }[];
      }>("/api/voice-profiles/import-url-preview", {
        url: u,
        max_items: importMaxItems,
      });
      const fetched: SourceDraft[] = res.samples.map((s) => ({
        source_title: s.source_title ?? "",
        content: s.content,
      }));
      setSources((prev) => {
        const merged = [...prev];
        for (const item of fetched) {
          const emptyIdx = merged.findIndex((s) => s.content.trim().length === 0);
          if (emptyIdx >= 0) {
            merged[emptyIdx] = item;
          } else {
            merged.push(item);
          }
        }
        return merged;
      });
      toast.success(
        `Imported ${fetched.length} ${fetched.length === 1 ? "sample" : "samples"} — review before training.`
      );
      setShowImportUrl(false);
      setImportUrl("");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setImporting(false);
    }
  }

  const validSources = sources.filter((s) => s.content.trim().length >= 50);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) {
      setError("Give your voice a name.");
      return;
    }
    if (validSources.length < 3) {
      setError("Add at least 3 samples (≥ 50 chars each) so we can train.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ id: string; status: string }>(
        "/api/voice-profiles",
        {
          name: name.trim(),
          sources: validSources.map((s) => ({
            content: s.content,
            source_title: s.source_title.trim() || undefined,
          })),
        }
      );
      toast.success("Voice profile created — training started.");
      navigate(`/app/voices/${res.id}`);
    } catch (err) {
      const msg = apiErrorMessage(err);
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <button
        onClick={() => navigate("/app/voices")}
        className="mb-4 text-sm text-ink-500 hover:text-ink-900"
      >
        ← Voices
      </button>
      <h1 className="page-title mb-6">
        Train a new voice
      </h1>

      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className="mb-1 block text-sm font-medium text-ink-900">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My personal voice"
            className="input"
            maxLength={80}
            required
          />
          <p className="mt-1 text-xs text-ink-500">
            Tip: name it after the channel/brand it represents.
          </p>
        </div>

        <div>
          <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
            <label className="block text-sm font-medium text-ink-900">
              Sample writing
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowImportUrl((v) => !v)}
                className="btn-secondary text-sm"
              >
                {showImportUrl ? "Cancel" : "🔗 Import from URL"}
              </button>
              <span
                className={`text-xs ${
                  validSources.length >= 3 ? "text-emerald-600" : "text-ink-500"
                }`}
              >
                {validSources.length} / 3 minimum
              </span>
            </div>
          </div>
          <p className="mb-3 text-xs text-ink-500">
            Paste 3-10 examples of your past long-form content (essays, posts,
            newsletters). The more authentic, the better. We'll extract the
            structural style — never the topics.
          </p>

          {showImportUrl && (
            <div className="mb-4 space-y-2 rounded-xl border border-ink-200 bg-white p-4">
              <input
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="https://example.com/blog/post  or  https://example.com/feed.xml"
                className="input"
                maxLength={1000}
                type="url"
              />
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs text-ink-500">
                  Max items:
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={importMaxItems}
                    onChange={(e) =>
                      setImportMaxItems(
                        Math.max(1, Math.min(10, Number(e.target.value) || 5))
                      )
                    }
                    className="ml-2 w-16 rounded border border-ink-300 px-2 py-1 text-sm"
                  />
                </label>
                <p className="flex-1 text-xs text-ink-500">
                  Paste an article URL or RSS/Atom feed. We'll extract clean text
                  into the samples below — you can review and edit before training.
                </p>
                <button
                  type="button"
                  onClick={importFromUrl}
                  className="btn-primary"
                  disabled={importing || importUrl.trim().length === 0}
                >
                  {importing ? "Importing…" : "Import"}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {sources.map((s, i) => (
              <div key={i} className="rounded-xl border border-ink-200 bg-white p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-ink-700">
                    Sample {i + 1}
                  </span>
                  {sources.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="text-xs text-ink-500 hover:text-red-600"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  value={s.source_title}
                  onChange={(e) => update(i, { source_title: e.target.value })}
                  placeholder="Title (optional)"
                  className="input mb-2"
                  maxLength={200}
                />
                <textarea
                  value={s.content}
                  onChange={(e) => update(i, { content: e.target.value })}
                  placeholder="Paste your content (≥ 50 chars)…"
                  className="input min-h-[180px] font-mono text-sm"
                  maxLength={100000}
                />
                <p className="mt-1 text-xs text-ink-500">
                  {s.content.length} chars{" "}
                  {s.content.length > 0 && s.content.length < 50
                    ? "· too short"
                    : ""}
                </p>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={add}
            className="btn-secondary mt-3"
            disabled={sources.length >= 10}
          >
            + Add another sample
          </button>
        </div>

        {error && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-ink-200 pt-4">
          <button
            type="button"
            onClick={() => navigate("/app/voices")}
            className="btn-secondary"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || validSources.length < 3 || name.trim().length === 0}
          >
            {submitting ? "Training…" : `Train voice (${validSources.length} samples)`}
          </button>
        </div>
      </form>
    </div>
  );
}
